/**
 * O FECHAMENTO DO LOTE — o veredito do ciclo, na tela, no PDF e na planilha.
 *
 * Proposta e decisões em `docs/FECHAMENTO.md` (06/10/2026). A conta mora em
 * `src/dominio/fechamento.ts`, com teste; esta tela só mostra.
 *
 * QUEM VÊ O QUÊ — decisão do gestor: os índices zootécnicos são para todos
 * («força o aprendizado»); dinheiro, só gestor e ADM. Quem decide é o
 * SERVIDOR (`ve_valores`): para o tratador o preço nem sai do banco.
 *
 * UM PAPEL POR VEZ NA PÁGINA. Aberto o romaneio de uma venda, o papel do
 * fechamento sai de cena — dois `.papel` montados sairiam na mesma folha.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  parametrosDoCalculo, verFechamento,
  type FechamentoBruto, type ParametrosDoCalculo,
} from '../acesso/api'
import { fecharLote, peixesDe, type Fechamento } from '../dominio/fechamento'
import { emReais, romaneioGravado } from '../dominio/romaneio'
import { baixarCsv, nomeDeArquivo } from '../dominio/planilha'
import { diaCurto, inteiro, numero } from '../dominio/unidades'
import { Dado, emitidoEm, porExtenso, SaidasDePapel } from './Papel'
import { RomaneioAberto } from './RomaneioAberto'
import type { CabecalhoDoRomaneio } from './RomaneioPapel'

const ROTULO_DA_FALTA: Record<string, string> = {
  justificada: 'justificadas',
  operacional: 'operacionais',
  critica: 'críticas',
  nao_classificada: 'não classificadas',
}

function pct(v: number | null): string {
  return v === null ? '—' : `${numero(v * 100, 1)}%`
}

function g(v: number | null): string {
  return v === null ? '—' : `${inteiro(v)} g`
}

function nomeDaRacao(r: FechamentoBruto['racao'][number]): string {
  if (r.granulometria_min_mm === null || r.proteina_pct === null) return 'sem especificação'
  const max = r.granulometria_max_mm ?? r.granulometria_min_mm
  const faixa = max === r.granulometria_min_mm
    ? `${numero(r.granulometria_min_mm, 1)} mm`
    : `${numero(r.granulometria_min_mm, 1)} a ${numero(max, 1)} mm`
  return `${faixa} · ${numero(r.proteina_pct, 0)}%`
}

/**
 * O cabeçalho do romaneio de cada venda. As vendas em si vêm do domínio
 * (`f.vendas`) — um agrupamento só, para a receita do lote e o valor de cada
 * venda nunca saírem de contas diferentes.
 */
function cabecalhoDaVenda(b: FechamentoBruto, chave: string, data: string): CabecalhoDoRomaneio {
  const linhas = b.movimentos.filter((m) => `${m.registrado_em}|${m.tipo}` === chave)
  return {
    represa: b.ciclo.tanque,
    ciclo: b.ciclo.codigo,
    dia: data,
    comprador: linhas.map((m) => m.comprador).find((x) => x) ?? '',
    destino: linhas.map((m) => m.destino).find((x) => x) ?? '',
    observacao: linhas.map((m) => m.observacao).filter((x) => x).join(' '),
  }
}

/** O limite de oxigênio que corta trato, lido das faixas da fazenda (regra 10). */
function limiteDeOxigenio(calculo: ParametrosDoCalculo | null): number | null {
  if (!calculo) return null
  const cortes = calculo.coeficientes
    .filter((c) => c.fator === 'oxigenio_mgl' && c.coeficiente < 1 && c.ate !== null)
    .map((c) => c.ate as number)
  return cortes.length > 0 ? Math.max(...cortes) : null
}

/**
 * Os indicadores numa lista só — é ela que vira o PDF e a planilha, para os
 * dois dizerem exatamente o mesmo.
 */
function linhasDoFechamento(
  b: FechamentoBruto,
  f: Fechamento,
  limite: number | null,
  diasAbaixo: number,
): [string, string][] {
  const ha = b.ciclo.area_m2 / 10_000
  const linhas: [string, string][] = [
    ['Peixes que entraram', inteiro(f.entraram.peixes)],
    ['Mortalidade registrada (piso)', inteiro(f.mortalidade)],
    ['Perdas de manejo', inteiro(f.perdas)],
    ['Tambaqui vendido', inteiro(f.vendidos.peixes)],
    ['Transferidos para outro tanque', inteiro(f.transferidos.peixes)],
    ...f.outrasEspecies.map((o): [string, string] => [
      `Outra espécie na rede: ${o.especie}`,
      `${inteiro(o.peixes)} peixes · ${numero(o.kg, 1)} kg`,
    ]),
    ['Sobrevivência (tambaqui)', pct(f.sobrevivencia)],
    ['Saldo residual (diagnóstico)', `${inteiro(f.residual)} peixes`],
    ['Peso médio de entrada', g(f.pesoEntradaG)],
    ['Peso médio de saída (tambaqui)', g(f.pesoSaidaG)],
    ['Ganho diário', f.ganhoGDia === null ? '—' : `${numero(f.ganhoGDia, 1)} g/dia`],
    ['Biomassa que entrou', `${numero(f.entraram.kg / 1000, 2)} t`],
    ['Biomassa que saiu (todas as espécies)', `${numero(f.biomassaSaidaKg / 1000, 2)} t`],
    ['Densidade final', f.densidadeTHa === null ? '—' : `${numero(f.densidadeTHa, 2)} t/ha`],
    ['Área', `${numero(ha, 2)} ha`],
    ['Ração', `${inteiro(f.racaoKg)} kg`],
    ['Conversão alimentar (esta engorda)', f.conversao === null ? '—' : numero(f.conversao, 2)],
    ['Ração por hectare por dia', f.kgHaDia === null ? '—' : `${numero(f.kgHaDia, 1)} kg/ha/dia`],
    ['Dias com trato', inteiro(f.diasComTrato)],
    ...f.faltas.map((x): [string, string] => [
      `Dias com falta ${ROTULO_DA_FALTA[x.classificacao] ?? x.classificacao}`,
      inteiro(x.dias),
    ]),
    ['% de dias sem tratar (métrica da planilha antiga)', pct(f.pctDiasSemTratar)],
  ]
  if (limite !== null && b.oxigenio.length > 0) {
    linhas.push([
      `Dias com oxigênio abaixo de ${numero(limite, 1)} mg/L (faixa vigente hoje)`,
      `${inteiro(diasAbaixo)} de ${inteiro(b.oxigenio.length)} dias lidos`,
    ])
  }
  if (b.ve_valores) {
    linhas.push(['Receita', f.receitaBrl === null ? 'incompleta — falta preço' : emReais(f.receitaBrl)])
    linhas.push(['Preço médio', f.precoMedioKg === null ? '—' : `${emReais(f.precoMedioKg)}/kg`])
    linhas.push(['Custo', 'ainda não lançado no sistema'])
  }
  return linhas
}

export function FechamentoDoLote({ cicloId }: { cicloId: string }) {
  const [bruto, setBruto] = useState<FechamentoBruto | null>(null)
  const [calculo, setCalculo] = useState<ParametrosDoCalculo | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  const [vendaAberta, setVendaAberta] = useState<string | null>(null)

  useEffect(() => {
    let vivo = true
    Promise.all([verFechamento(cicloId), parametrosDoCalculo().catch(() => null)])
      .then(([b, p]) => {
        if (!vivo) return
        setBruto(b)
        setCalculo(p)
      })
      .catch((falha: unknown) => {
        if (vivo) setErro(falha instanceof Error ? falha.message : 'não deu para carregar')
      })
    return () => {
      vivo = false
    }
  }, [cicloId])

  if (erro) return <p className="aviso-erro">O fechamento não carregou: {erro}</p>
  if (!bruto) return <p className="aviso">Carregando o fechamento…</p>

  const fim = bruto.encerramento?.data ?? null
  const f = fecharLote({
    data_inicio: bruto.ciclo.data_inicio,
    data_fim: fim,
    area_m2: bruto.ciclo.area_m2,
    movimentos: bruto.movimentos,
    racao_kg: bruto.tratos.kg,
    dias_com_trato: bruto.tratos.dias,
    faltas: bruto.faltas,
    dias_com_falta: bruto.dias_com_falta,
  })
  const limite = limiteDeOxigenio(calculo)
  const diasAbaixo = limite === null ? 0 : bruto.oxigenio.filter((o) => o.oxigenio_mgl < limite).length
  const vendas = f.vendas.map((v) => ({
    ...v,
    romaneio: romaneioGravado(v.linhas, v.descontoUmidade),
    cabecalho: cabecalhoDaVenda(bruto, v.chave, v.data),
  }))
  const aberta = vendas.find((v) => v.chave === vendaAberta) ?? null
  // «Despesca final» só quando houve despesca final: um ciclo encerrado por
  // transferência não vendeu nada, e o papel não pode dizer que vendeu.
  const houveFinal = bruto.movimentos.some((m) => m.tipo === 'despesca_final')
  const rotuloDoFim = houveFinal ? 'Despesca final' : 'Encerramento'
  const ve = bruto.ve_valores
  const linhas = linhasDoFechamento(bruto, f, limite, diasAbaixo)
  const entradas = bruto.movimentos.filter((m) => m.tipo === 'povoamento' || m.tipo === 'transferencia_entrada')

  function planilha(): boolean {
    return baixarCsv(nomeDeArquivo('fechamento', bruto?.ciclo.codigo ?? 'lote', fim ?? ''), [
      ['Fechamento de lote'],
      ['Fazenda', 'Cunhataí'],
      ['Represa', bruto?.ciclo.tanque ?? ''],
      ['Ciclo', bruto?.ciclo.codigo ?? ''],
      ['Entrada no tanque', diaCurto(bruto?.ciclo.data_inicio ?? '')],
      [rotuloDoFim, fim ? diaCurto(fim) : ''],
      ['Dias no tanque', f.dias],
      ['Dias desde o alevino', f.diasDesdeAlevino],
      [],
      ...linhas,
    ])
  }

  return (
    <div className="fechamento">
      <h3>Fechamento do lote</h3>
      {!fim && (
        <p className="aviso-erro">
          Este ciclo não tem encerramento registrado — os números abaixo contam até agora.
        </p>
      )}

      <div className="ficha-linha">
        <span>no tanque</span>
        <span className="ficha-valor">
          {diaCurto(bruto.ciclo.data_inicio)} a {fim ? diaCurto(fim) : 'hoje'}
          {f.dias !== null && <> · {inteiro(f.dias)} dias</>}
        </span>
      </div>
      <div className="ficha-linha">
        <span>desde o alevino</span>
        <span className="ficha-valor">
          {f.diasDesdeAlevino !== null ? `${inteiro(f.diasDesdeAlevino)} dias` : 'origem não rastreada'}
        </span>
      </div>

      <h4>Peixes</h4>
      {entradas.map((m, i) => (
        <div className="ficha-linha" key={`entrada-${i}`}>
          <span>
            entrou {diaCurto(m.data)} ·{' '}
            {m.remessa ? `remessa ${m.remessa}` : m.contraparte ? `do ciclo ${m.contraparte}` : 'origem não registrada'}
          </span>
          <span className="ficha-valor">
            {inteiro(peixesDe(m))}{m.quantidade === null && <span className="secundario"> (pesado)</span>}
            {' · '}{inteiro(m.peso_medio_g)} g
          </span>
        </div>
      ))}
      <div className="ficha-linha">
        <span>mortalidade registrada</span>
        <span className="ficha-valor">{inteiro(f.mortalidade)}</span>
      </div>
      {f.perdas > 0 && (
        <div className="ficha-linha">
          <span>perdas de manejo</span>
          <span className="ficha-valor">{inteiro(f.perdas)}</span>
        </div>
      )}
      <div className="ficha-linha">
        <span>tambaqui vendido</span>
        <span className="ficha-valor">{inteiro(f.vendidos.peixes)}</span>
      </div>
      {f.transferidos.peixes > 0 && (
        <div className="ficha-linha">
          <span>transferidos para outro tanque</span>
          <span className="ficha-valor">{inteiro(f.transferidos.peixes)}</span>
        </div>
      )}
      {f.outrasEspecies.map((o) => (
        <div className="ficha-linha" key={o.especie}>
          <span>{o.especie} na rede (fora da sobrevivência)</span>
          <span className="ficha-valor">{inteiro(o.peixes)} · {numero(o.kg, 1)} kg</span>
        </div>
      ))}
      <div className="ficha-linha forte">
        <span>sobrevivência</span>
        <span className="ficha-valor">{pct(f.sobrevivencia)}</span>
      </div>
      <div className="ficha-linha">
        <span>saldo residual</span>
        <span className="ficha-valor">{inteiro(f.residual)} peixes</span>
      </div>
      <p className="secundario">
        O residual é diagnóstico, não acusação: nem todo peixe morto boia, e a predação não deixa
        nada para contar — é aqui que as duas aparecem.
        {bruto.encerramento?.saldo_residual_mostrado != null && (
          <> Antes da despesca, a estimativa do app era de{' '}
            {inteiro(bruto.encerramento.saldo_residual_mostrado)} peixes na represa.</>
        )}
      </p>

      <h4>Peso e densidade</h4>
      <div className="ficha-linha">
        <span>peso médio, entrada → saída</span>
        <span className="ficha-valor">{g(f.pesoEntradaG)} → {g(f.pesoSaidaG)}</span>
      </div>
      <div className="ficha-linha">
        <span>ganho diário</span>
        <span className="ficha-valor">{f.ganhoGDia === null ? '—' : `${numero(f.ganhoGDia, 1)} g/dia`}</span>
      </div>
      <div className="ficha-linha forte">
        <span>densidade final</span>
        <span className="ficha-valor">
          {f.densidadeTHa === null ? '—' : `${numero(f.densidadeTHa, 2)} t/ha`}
          {bruto.ciclo.carga_final_alvo_t_ha !== null && (
            <span className="secundario"> · alvo {numero(bruto.ciclo.carga_final_alvo_t_ha, 1)}</span>
          )}
        </span>
      </div>

      <h4>Ração</h4>
      <div className="ficha-linha forte">
        <span>conversão alimentar</span>
        <span className="ficha-valor">{f.conversao === null ? '—' : numero(f.conversao, 2)}</span>
      </div>
      <div className="ficha-linha">
        <span>ração total</span>
        <span className="ficha-valor">{inteiro(f.racaoKg)} kg</span>
      </div>
      {bruto.racao.length > 1 && bruto.racao.map((r, i) => (
        <div className="ficha-linha" key={`racao-${i}`}>
          <span className="secundario">{nomeDaRacao(r)}</span>
          <span className="ficha-valor secundario">{inteiro(r.kg)} kg</span>
        </div>
      ))}
      <div className="ficha-linha">
        <span>por hectare por dia</span>
        <span className="ficha-valor">{f.kgHaDia === null ? '—' : `${numero(f.kgHaDia, 1)} kg`}</span>
      </div>

      <h4>Trato e água</h4>
      <div className="ficha-linha">
        <span>dias com trato</span>
        <span className="ficha-valor">{inteiro(f.diasComTrato)}</span>
      </div>
      {f.faltas.map((x) => (
        <div className="ficha-linha" key={x.classificacao}>
          <span>faltas {ROTULO_DA_FALTA[x.classificacao] ?? x.classificacao}</span>
          <span className="ficha-valor">{inteiro(x.dias)} dias</span>
        </div>
      ))}
      <div className="ficha-linha">
        <span className="secundario">% de dias sem tratar, como na planilha antiga</span>
        <span className="ficha-valor secundario">{pct(f.pctDiasSemTratar)}</span>
      </div>
      {limite !== null && bruto.oxigenio.length > 0 && (
        <div className="ficha-linha">
          <span>oxigênio abaixo de {numero(limite, 1)} mg/L <span className="secundario">(faixa de hoje)</span></span>
          <span className="ficha-valor">{inteiro(diasAbaixo)} de {inteiro(bruto.oxigenio.length)} dias lidos</span>
        </div>
      )}

      <h4>Vendas</h4>
      {vendas.length === 0 && <p className="secundario">Nenhuma despesca registrada neste ciclo.</p>}
      {vendas.map((v) => (
        <div key={v.chave}>
          <div className="ficha-linha">
            <span>
              {diaCurto(v.data)} · {v.encerrou ? 'final' : 'parcial'}
              {v.cabecalho.comprador && <span className="secundario"> · {v.cabecalho.comprador}</span>}
            </span>
            <span className="ficha-valor">
              {v.romaneio ? `${numero(v.romaneio.pesadoKg, 1)} kg` : '—'}
              {ve && v.romaneio?.valorBrl != null && <> · {emReais(v.romaneio.valorBrl)}</>}
            </span>
          </div>
          {v.romaneio && (
            <button
              type="button"
              className="discreto"
              onClick={() => setVendaAberta(vendaAberta === v.chave ? null : v.chave)}
            >
              {vendaAberta === v.chave ? 'fechar o romaneio' : 'abrir o romaneio'}
            </button>
          )}
          {aberta?.chave === v.chave && aberta.romaneio && (
            <RomaneioAberto romaneio={aberta.romaneio} cabecalho={aberta.cabecalho} semValores={!ve} />
          )}
        </div>
      ))}
      {ve && (
        <>
          <div className="ficha-linha forte">
            <span>receita</span>
            <span className="ficha-valor">
              {f.receitaBrl === null ? (vendas.length > 0 ? 'incompleta — falta preço' : '—') : emReais(f.receitaBrl)}
            </span>
          </div>
          {f.precoMedioKg !== null && (
            <div className="ficha-linha">
              <span>preço médio</span>
              <span className="ficha-valor">{emReais(f.precoMedioKg)}/kg</span>
            </div>
          )}
          <h4>Custo</h4>
          <p className="secundario">
            Custo ainda não lançado no sistema — alevino, ração por trato e o resto entram numa
            etapa própria (ver docs/FECHAMENTO.md).
          </p>
        </>
      )}

      {bruto.encerramento?.quem && (
        <p className="secundario">
          Encerrado por {bruto.encerramento.quem}
          {bruto.encerramento.observacao ? ` — ${bruto.encerramento.observacao}` : ''}.
        </p>
      )}

      {/* O papel do fechamento só sem romaneio aberto: um papel por vez. */}
      {!aberta && (
        <>
          <SaidasDePapel aoPlanilha={planilha} />
          <FechamentoPapel bruto={bruto} f={f} linhas={linhas} rotuloDoFim={rotuloDoFim} />
        </>
      )}
    </div>
  )
}

function FechamentoPapel({
  bruto, f, linhas, rotuloDoFim,
}: {
  bruto: FechamentoBruto
  f: Fechamento
  linhas: [string, string][]
  rotuloDoFim: string
}) {
  const fim = bruto.encerramento?.data ?? null
  return createPortal(
    <div className="papel romaneio-papel">
      <header className="rp-capa">
        <div>
          <div className="rp-antetitulo">Sistema de Gestão · Piscicultura</div>
          <h1>Fazenda Cunhataí</h1>
          <div className="rp-lugar">Ariquemes — Rondônia</div>
        </div>
        <div className="rp-selo">
          <div className="rp-selo-titulo">Fechamento de lote</div>
          <div className="rp-selo-data">{fim ? porExtenso(fim) : 'em aberto'}</div>
        </div>
      </header>

      <section className="rp-ficha">
        <Dado rotulo="Represa" valor={bruto.ciclo.tanque} />
        <Dado rotulo="Ciclo" valor={bruto.ciclo.codigo} />
        <Dado rotulo="Entrada no tanque" valor={porExtenso(bruto.ciclo.data_inicio)} />
        <Dado rotulo={rotuloDoFim} valor={fim ? porExtenso(fim) : '—'} />
        <Dado rotulo="Dias no tanque" valor={f.dias === null ? '—' : inteiro(f.dias)} />
        <Dado
          rotulo="Dias desde o alevino"
          valor={f.diasDesdeAlevino === null ? 'origem não rastreada' : inteiro(f.diasDesdeAlevino)}
        />
      </section>

      <table className="rp-tabela">
        <thead>
          <tr>
            <th>Indicador</th>
            <th>Valor</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map(([rotulo, valor]) => (
            <tr key={rotulo}>
              <td className="rp-especie">{rotulo}</td>
              <td>{valor}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <footer className="rp-rodape">
        {emitidoEm()} pelo sistema de gestão da Fazenda Cunhataí. O saldo residual é diagnóstico:
        nem todo peixe morto boia, e a predação não deixa registro.
      </footer>
    </div>,
    document.body,
  )
}
